import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary",
  {
    variants: {
      variant: {
        // Primary: Solid blue, white text, scale on hover
        default: "bg-primary text-white hover:bg-primary-hover hover:scale-105 active:scale-100",
        // Destructive: Solid red, scale on hover
        destructive:
          "bg-destructive text-white hover:bg-red-600 hover:scale-105 active:scale-100 focus-visible:ring-destructive",
        // Outline: Thick border, fill on hover (flat design signature)
        outline:
          "border-4 border-primary bg-transparent text-primary hover:bg-primary hover:text-white hover:scale-105 active:scale-100",
        // Secondary: Gray background, subtle hover
        secondary: "bg-muted text-foreground hover:bg-muted-hover hover:scale-105 active:scale-100",
        // Ghost: No background, subtle fill on hover
        ghost: "bg-transparent text-foreground hover:bg-muted hover:scale-105 active:scale-100",
        // Link: Simple underline effect
        link: "text-primary underline-offset-4 hover:underline bg-transparent",
      },
      size: {
        default: "h-10 px-5 py-2 has-[>svg]:px-4",
        sm: "h-8 rounded-md gap-1.5 px-4 has-[>svg]:px-3 text-xs",
        lg: "h-12 rounded-md px-8 has-[>svg]:px-6 text-base",
        xl: "h-14 rounded-md px-10 has-[>svg]:px-8 text-lg",
        icon: "size-10",
        "icon-sm": "size-8",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
